import { Box, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Ícone com tooltip que explica a fonte dos dados, a regra de cálculo
 * e o comportamento de cada card/gráfico do Dashboard de forma humanizada.
 *
 * Regras gerais (válidas para todos os cards de "previsto" — faturamento,
 * receita e fases):
 * - A base de dados é o último Resumo Financeiro de cada projeto
 *   (coleção projectfinancialsummaries), a mesma usada na aba "Resumo"
 *   dentro do projeto. Quando o projeto não tem resumo, caímos para os
 *   campos legados do próprio projeto (customerBilling / totalEvent).
 * - Projetos com status Orçamento (BUDGET) ficam na fase Briefing;
 *   o faturamento previsto sai do orçamento do cliente informado no
 *   briefing e a receita prevista é zero (ainda sem precificação).
 * - Sem filtro de status aplicado, projetos Perdido, Cancelado,
 *   Declinado e Duplicidade são removidos de TODOS os cálculos
 *   (incluindo a contagem de MMs). Quando o usuário seleciona
 *   explicitamente um desses status no filtro, eles entram normalmente
 *   com seus valores — a escolha do usuário tem prioridade.
 * - A contagem de MMs agora bate com a soma das fases e com a lista
 *   mensal, pois todos usam o mesmo recorte de pipeline produtivo
 *   (a menos que o usuário inclua status não-produtivos via filtro).
 */

const BASE_INFO = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>
    <strong>Fonte:</strong> último <strong>Resumo Financeiro</strong> de cada projeto
    (coleção <em>projectfinancialsummaries</em>), a mesma base da aba "Resumo".
    Projetos sem resumo usam os campos legados do projeto (faturamento e receita
    inseridos manualmente).
  </Typography>
);

const EXCLUSION_NOTE = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1 }}>
    <strong>Status não-produtivos:</strong> sem filtro de status aplicado, projetos
    com status <strong>Perdido</strong>, <strong>Cancelado</strong>,{" "}
    <strong>Declinado</strong>, <strong>Duplicidade</strong> e variantes são
    removidos de <strong>todos os cálculos</strong> (faturamento, receita,
    contagem de MMs e fases). Quando você seleciona explicitamente um desses
    status no filtro, eles entram com seus valores reais.
  </Typography>
);

const BUDGET_NOTE = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1 }}>
    <strong>Orçamento (BUDGET):</strong> projetos em orçamento ficam na fase{" "}
    <strong>Briefing</strong>. O faturamento vem do{" "}
    <strong>orçamento do cliente</strong> informado no briefing; a receita
    prevista é <strong>zero</strong> enquanto não há precificação detalhada.
  </Typography>
);

const BILLING_DETAIL = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1 }}>
    <strong>Como o faturamento é calculado:</strong>
    <br />
    • Fase Briefing: usa o <em>clientBudget</em> do resumo financeiro ou o{" "}
    <em>customerBudget</em> do briefing.
    <br />
    • Demais fases (Em Aprovação, Aprovado, Fechamento, Auditado): usa o{" "}
    <em>totalValueOfEvents</em> do resumo financeiro.
  </Typography>
);

const REVENUE_DETAIL = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1 }}>
    <strong>Como a receita é calculada:</strong>
    <br />
    Soma de 12 componentes do resumo financeiro: taxas logísticas e aéreas,
    markups, overheads, comissões, comunicação, RSVP e tributos adicionais.
    Projetos sem resumo usam o campo legado <em>totalEvent</em> do projeto.
  </Typography>
);

const MMS_DETAIL = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1 }}>
    <strong>Contagem de MMs:</strong> cada projeto conta <strong>uma única vez</strong>{" "}
    no mês da data de início do evento (sem duplicar eventos multi-mês).
    A soma das barras do gráfico de MMs por mês bate com o número grande
    do card, pois ambos usam o mesmo recorte. Sem filtro de status, apenas
    projetos produtivos são contados.
  </Typography>
);

const AUDITED_DETAIL = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1 }}>
    <strong>Projetos auditados:</strong> contagem de projetos cujo status é{" "}
    <strong>Auditado</strong>. Um projeto pode estar em qualquer fase e ser
    contado como auditado. Este número é independente das fases exibidas nos
    cards de faturamento e receita.
  </Typography>
);

const MONTHLY_MMS_DETAIL = (
  <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1 }}>
    <strong>MMs por mês:</strong> cada projeto é contado no mês da sua{" "}
    <strong>data de início</strong>. A deduplicação é feita por MM + ano
    (cada MM aparece no máximo uma vez por mês). O ano corrente é inferido
    do filtro de data ou do calendário do servidor. Sem filtro de status,
    projetos não-produtivos (Perdido, Cancelado, etc.) são excluídos.
  </Typography>
);

interface DashboardSourceInfoProps {
  /** Cor do ícone (precisa contrastar com o fundo). Default: rgba(0,0,0,0.54). */
  color?: string;
  /** Tamanho do ícone em px. Default: 16. */
  size?: number;
  /** Texto extra acima do bloco padrão (ex: "Faturamento previsto"). */
  context?: string;
  /** Quando true, omite a observação sobre exclusão de perdidos/cancelados. */
  hideExpectedNote?: boolean;
}

export const DashboardSourceInfo = ({
  color = "rgba(0,0,0,0.54)",
  size = 16,
  context,
  hideExpectedNote = false,
}: DashboardSourceInfoProps) => {
  const isBilling = context?.toLowerCase().includes("faturamento");
  const isRevenue = context?.toLowerCase().includes("receita");
  const isMMsTotal = context?.toLowerCase().includes("pipeline de projetos");
  const isMMsChart = context?.toLowerCase().includes("mês da data de início");
  const isAudited = context?.toLowerCase().includes("auditado");
  const isBillingRevenueList = context?.toLowerCase().includes("por projeto");

  const showExclusionNote = !hideExpectedNote;
  const showBudgetNote = !hideExpectedNote && (isBilling || isRevenue || isBillingRevenueList);

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5, maxWidth: 380 }}>
          {context ? (
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
              {context}
            </Typography>
          ) : null}

          {BASE_INFO}

          {isBilling && BILLING_DETAIL}
          {isRevenue && REVENUE_DETAIL}
          {(isMMsTotal || isMMsChart) && MMS_DETAIL}
          {isMMsChart && MONTHLY_MMS_DETAIL}
          {isAudited && AUDITED_DETAIL}

          {showExclusionNote && EXCLUSION_NOTE}
          {showBudgetNote && BUDGET_NOTE}
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "help",
          color,
          opacity: 0.85,
          "&:hover": { opacity: 1 },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: size }} />
      </Box>
    </Tooltip>
  );
};
